/**
 * Strona Kontakt PPHU Imperium
 * Design: Industrial Precision
 * Sekcje: Hero, Dane kontaktowe, Mapa Google, Formularz
 */
import { useState } from 'react';
import { useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, ExternalLink, Send, CheckCircle2 } from 'lucide-react';
import { companyData } from '@/theme';
import { trpc } from '@/lib/trpc';
import { Link } from 'wouter';
import CmsContactHeroDescription from '@/components/CmsContactHeroDescription';

const serviceOptions = [
  'Elektronarzędzia Milwaukee',
  'Pneumatyka',
  'Narzędzia warsztatowe',
  'Materiały ścierne',
  'Narzędzia skrawające',
  'Narzędzia pomiarowe',
  'Chemia techniczna',
  'Materiały spawalnicze',
  'Śruby, nakrętki, łożyska',
  'Inne / zapytanie ogólne',
];

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [rodoConsent, setRodoConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const submitContact = trpc.contact.submit.useMutation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rodoConsent) {
      setSendError('Proszę zaznaczyć zgodę na przetwarzanie danych osobowych.');
      return;
    }
    // Rate limit: max 1 wysyłka na 60 sekund
    if (lastSentAt && Date.now() - lastSentAt < 60000) {
      setSendError('Proszę poczekać chwilę przed ponownym wysłaniem formularza.');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      // honeypot – pole ukryte, boty je wypełniają
      const honeypot = (document.getElementById('_gotcha') as HTMLInputElement)?.value;
      if (honeypot) {
        // Bot wykryty – udajemy sukces
        setSubmitted(true);
        setSending(false);
        return;
      }
      await submitContact.mutateAsync({
        name: formData.name,
        phone: formData.phone,
        service: formData.service || 'Nie wybrano',
        message: formData.message,
      });
      setSubmitted(true);
      setLastSentAt(Date.now());
    } catch {
      setSendError('Wystąpił błąd podczas wysyłania. Proszę spróbować ponownie lub zadzwonić bezpośrednio.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>

      {/* ============================================================
          HERO
          ============================================================ */}
      <section style={{
        backgroundColor: '#1A2744',
        padding: '4rem 0 3.5rem',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(245,196,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(245,196,0,0.04) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} aria-hidden="true" />
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: '#F5C400' }} aria-hidden="true" />
        <div className="container" style={{ position: 'relative' }}>
          <div className="section-label">Jesteśmy do dyspozycji</div>
          <h1 style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            textTransform: 'uppercase',
            color: '#F8F8F6',
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
            marginBottom: '1rem',
          }}>
            Skontaktuj się z nami {' '}
            <span style={{ color: '#F5C400' }}>PPHU Imperium</span>
          </h1>
          <p style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: '1.05rem',
            color: 'rgba(248,248,246,0.7)',
            lineHeight: 1.75,
            maxWidth: '560px',
          }}>
            <CmsContactHeroDescription />
          </p>
        </div>
      </section>

      {/* ============================================================
          DANE KONTAKTOWE + FORMULARZ
          ============================================================ */}
      <section style={{ backgroundColor: '#F8F8F6', padding: '5rem 0' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '4rem',
            alignItems: 'start',
          }}>

            {/* Dane kontaktowe */}
            <div>
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: '1.75rem',
                textTransform: 'uppercase',
                color: '#0D0D0D',
                letterSpacing: '-0.01em',
                marginBottom: '2rem',
              }}>
                Dane kontaktowe
              </h2>

              {/* NAP Name Address Phone */}
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
                itemScope
                itemType="https://schema.org/LocalBusiness"
              >
                <meta itemProp="name" content="PPHU Imperium Robert Gostomczyk" />

                {/* Address */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1.25rem',
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderLeft: '3px solid #F5C400',
                  borderRadius: '2px',
                }}>
                  <MapPin size={22} color="#F5C400" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6B7280', marginBottom: '0.375rem' }}>
                      Adres
                    </div>
                    <address
                      style={{ fontStyle: 'normal' }}
                      itemProp="address"
                      itemScope
                      itemType="https://schema.org/PostalAddress"
                    >
                      <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: '1rem', color: '#0D0D0D', lineHeight: 1.5 }}>
                        <span itemProp="name">PPHU Imperium Robert Gostomczyk</span>
                      </div>
                      <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: '0.9rem', color: '#374151', lineHeight: 1.6 }}>
                        <span itemProp="streetAddress">{companyData.address.street}</span><br />
                        <span itemProp="addressLocality">{companyData.address.city}</span>
                        , <span itemProp="postalCode">{companyData.address.zip}</span>
                      </div>
                    </address>
                    <a
                      href={companyData.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        marginTop: '0.5rem',
                        fontSize: '0.8rem',
                        color: '#1A2744',
                        textDecoration: 'none',
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      <ExternalLink size={12} />
                      Otwórz w Google Maps
                    </a>
                  </div>
                </div>

                {/* Phone 1 */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1.25rem',
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderLeft: '3px solid #F5C400',
                  borderRadius: '2px',
                }}>
                  <Phone size={22} color="#F5C400" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                   <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6B7280', marginBottom: '0.375rem' }}>
                      Telefon
                    </div>
                    <a
                      href={`tel:${companyData.phoneRaw}`}
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        color: '#0D0D0D',
                        textDecoration: 'none',
                        letterSpacing: '0.02em',
                      }}
                      itemProp="telephone"
                    >
                      {companyData.phone}
                    </a>
                  </div>
                </div>

                {/* Phone 2 */}
                {/* Email */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1.25rem',
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderLeft: '3px solid #F5C400',
                  borderRadius: '2px',
                }}>
                  <Mail size={22} color="#F5C400" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6B7280', marginBottom: '0.375rem' }}>
                      E-mail
                    </div>
                    <a
                      href={`mailto:${companyData.email}`}
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#0D0D0D',
                        textDecoration: 'none',
                      }}
                      itemProp="email"
                    >
                      {companyData.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1.25rem',
                  backgroundColor: '#FFF9E0',
                  border: '1px solid rgba(245,196,0,0.3)',
                  borderLeft: '3px solid #F5C400',
                  borderRadius: '2px',
                }}>
                  <Clock size={22} color="#F5C400" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6B7280', marginBottom: '0.375rem' }}>
                      Godziny otwarcia
                    </div>
                    <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: '0.9rem', color: '#374151', lineHeight: 1.6 }}>
                      Pon Pt: 08:00 15:45<br />Sb / Nd: Zamknięte
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Formularz kontaktowy */}
            <div>
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: '1.75rem',
                textTransform: 'uppercase',
                color: '#0D0D0D',
                letterSpacing: '-0.01em',
                marginBottom: '0.5rem',
              }}>
                Formularz kontaktowy
              </h2>
              <p style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '0.875rem',
                color: '#6B7280',
                marginBottom: '2rem',
              }}>
                
              </p>

              {submitted ? (
                <div style={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #86EFAC',
                  borderLeft: '3px solid #22C55E',
                  borderRadius: '2px',
                  padding: '2rem',
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'flex-start',
                }}>
                  <CheckCircle2 size={24} color="#22C55E" style={{ flexShrink: 0 }} />
                  <div>
                <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: '#15803D', marginBottom: '0.375rem' }}>
                      Wiadomość wysłana!
                    </div>
                    <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: '0.875rem', color: '#166534', margin: 0 }}>
                      Dziękujemy za wiadomość. Skontaktujemy się z Tobą wkrótce.
                      Możesz też zadzwonić bezpośrednio: <a href={`tel:${companyData.phoneRaw}`} style={{ color: '#15803D', fontWeight: 600 }}>{companyData.phone}</a>
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {/* Honeypot – ukryte pole anty-spam */}
                  <input id="_gotcha" name="_gotcha" type="text" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      style={{ display: 'block', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#374151', marginBottom: '0.5rem' }}
                    >
                      Imię i nazwisko *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jan Kowalski"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '0.95rem',
                        color: '#0D0D0D',
                        backgroundColor: '#fff',
                        border: '1px solid #D1D5DB',
                        borderRadius: '2px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 150ms ease',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#F5C400'; e.target.style.boxShadow = '0 0 0 3px rgba(245,196,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      style={{ display: 'block', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#374151', marginBottom: '0.5rem' }}
                    >
                      Telefon *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="500 000 000"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '0.95rem',
                        color: '#0D0D0D',
                        backgroundColor: '#fff',
                        border: '1px solid #D1D5DB',
                        borderRadius: '2px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 150ms ease',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#F5C400'; e.target.style.boxShadow = '0 0 0 3px rgba(245,196,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>

                  {/* Service */}
                  <div>
                    <label
                      htmlFor="service"
                      style={{ display: 'block', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#374151', marginBottom: '0.5rem' }}
                    >
                      Czego szukasz?
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '0.95rem',
                        color: formData.service ? '#0D0D0D' : '#9CA3AF',
                        backgroundColor: '#fff',
                        border: '1px solid #D1D5DB',
                        borderRadius: '2px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        appearance: 'none',
                        cursor: 'pointer',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#F5C400'; e.target.style.boxShadow = '0 0 0 3px rgba(245,196,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
                    >
                      <option value="">Wybierz kategorię produktu...</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      style={{ display: 'block', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#374151', marginBottom: '0.5rem' }}
                    >
                      Wiadomość
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Opisz czego potrzebujesz model, ilość, zastosowanie..."
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '0.95rem',
                        color: '#0D0D0D',
                        backgroundColor: '#fff',
                        border: '1px solid #D1D5DB',
                        borderRadius: '2px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                        lineHeight: 1.6,
                        transition: 'border-color 150ms ease',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#F5C400'; e.target.style.boxShadow = '0 0 0 3px rgba(245,196,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={sending || !rodoConsent}
                    style={{ alignSelf: 'flex-start', fontSize: '1rem', padding: '0.875rem 2rem', opacity: (sending || !rodoConsent) ? 0.6 : 1, cursor: (sending || !rodoConsent) ? 'not-allowed' : 'pointer' }}
                  >
                    <Send size={16} />
                    {sending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                  </button>
                  {/* Zgoda RODO */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
                    <input
                      id="rodo"
                      type="checkbox"
                      checked={rodoConsent}
                      onChange={(e) => setRodoConsent(e.target.checked)}
                      style={{ marginTop: '3px', flexShrink: 0, accentColor: '#F5C400', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <label htmlFor="rodo" style={{ fontSize: '0.78rem', color: '#6B7280', fontFamily: "'Manrope', sans-serif", lineHeight: 1.6, cursor: 'pointer' }}>
                      * Wyrażam zgodę na przetwarzanie moich danych osobowych (imię, telefon, treść wiadomości) przez Robert Gostomczyk P.P.H.U. „IMPERIUM" w celu odpowiedzi na moje zapytanie, zgodnie z{' '}
                      <Link href="/polityka-prywatnosci" style={{ color: '#1A2744', fontWeight: 600, textDecoration: 'underline' }}>
                        Polityką prywatności
                      </Link>
                      . Zgoda jest dobrowolna i może być cofnięta w dowolnym momencie.
                    </label>
                  </div>
                  {sendError && (
                    <p style={{ fontSize: '0.8rem', color: '#C41E1E', fontFamily: "'Manrope', sans-serif", margin: 0 }}>
                      {sendError}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          MAPA GOOGLE
          ============================================================ */}
      <section style={{ backgroundColor: '#0D0D0D', padding: '0' }}>
        <div style={{
          borderTop: '3px solid #F5C400',
          padding: '3rem 0 0',
        }}>
          <div className="container" style={{ marginBottom: '1.5rem' }}>
            <div className="section-label">Znajdź nas</div>
            <h2 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              textTransform: 'uppercase',
              color: '#F8F8F6',
              letterSpacing: '-0.01em',
              marginBottom: '0.5rem',
            }}>
              PPHU Imperium ul. Kosynierów 21, Sławno
            </h2>
            <p style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: '0.875rem',
              color: 'rgba(248,248,246,0.5)',
            }}>
              Województwo zachodniopomorskie · Polska
            </p>
          </div>
          <div className="container" style={{ paddingBottom: '3rem' }}>
            <div style={{
              borderRadius: '4px',
              overflow: 'hidden',
              border: '2px solid rgba(245,196,0,0.35)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            }}>
              <iframe
                src="https://maps.google.com/maps?cid=2620159158954562091&output=embed&hl=pl&z=16"
                width="100%"
                height="420"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="PPHU Imperium Robert Gostomczyk Kosynierów 21, Sławno"
              />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
